import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import moment from 'moment';

import { CircularProgress, Grid } from '@mui/material';
import DateRangePicker from '~/components/DateRangePicker';
import Header from '~/components/Header';
import { maisVendasApi } from '~/services/api';
import Snackbar from '~/util/SnackBar';
import {
    changeDatePeriod,
    changeInitialDate,
    changeFinalDate,
} from '~/store/modules/status/actions';
import { GERENTE_SOMA, VENDEDOR_SOMA } from '~/util/perfilId';
import { FOXTON } from '~/util/brands';
import getDefaultRouteForUser from '~/util/getDefaultRouteForUser';
import {
    mapApiFieldsToRow,
    STORE_REPORT_COLUMNS,
    VENDEDORES_REPORT_COLUMNS,
} from './constants';
import ReportSectionCard from './components/ReportSectionCard';
import ReportTable from './components/ReportTable';
import SSSGDetailsModal from './components/SSSGDetailsModal';
import * as Style from './styles';

function RealTimeNow() {
    const dispatch = useDispatch();
    const profile = useSelector(state => state.user.profile);
    const { initialDate, finalDate } = useSelector(state => state.status);
    const isGerenteFoxton =
        profile?.id_perfil === GERENTE_SOMA && profile?.marcaFilial === FOXTON;
    const isVendedorFoxton =
        profile?.id_perfil === VENDEDOR_SOMA && profile?.marcaFilial === FOXTON;

    const branchCode = profile?.users_branches?.[0]?.codigo_filial;
    const formattedBranchCode =
        branchCode && branchCode.length === 3 ? `000${branchCode}` : branchCode;
    const salesCode = profile?.users_sales_code?.[0]?.codigo_vendedor;
    const sellerName = profile?.nome ?? 'Vendedor';

    const [managerLoading, setManagerLoading] = useState(true);
    const [storeRow, setStoreRow] = useState(null);
    const [sellerRows, setSellerRows] = useState(null);
    const [sellerLoading, setSellerLoading] = useState(true);
    const [personalRow, setPersonalRow] = useState(null);

    const [sssgModal, setSssgModal] = useState({
        open: false,
        variant: 'store',
        row: null,
    });

    const openSssgModal = (row, variant) => {
        setSssgModal({ open: true, variant, row });
    };

    const closeSssgModal = () => {
        setSssgModal(currState => ({ ...currState, open: false }));
    };

    const checkIfCanMakeRequests = useCallback(() => {
        return !!(initialDate && finalDate);
    }, [finalDate, initialDate]);

    useEffect(() => {
        dispatch(changeDatePeriod('day'));
        dispatch(changeInitialDate(moment()));
        dispatch(changeFinalDate(moment()));
    }, [dispatch]);

    useEffect(() => {
        if (!isGerenteFoxton || !formattedBranchCode) return;
        if (!checkIfCanMakeRequests()) return;

        const fetchManagerReport = async () => {
            setManagerLoading(true);
            try {
                const response = await maisVendasApi.get(
                    'salesmanager/sales-report',
                    {
                        params: {
                            dateInit: moment(initialDate).format('YYYY-MM-DD'),
                            dateEnd: moment(finalDate).format('YYYY-MM-DD'),
                            branchCode: formattedBranchCode,
                        },
                    }
                );

                const { branch, ...salespeople } = response.data?.[0] || {};

                setStoreRow(
                    mapApiFieldsToRow('store-total', 'Total', branch, true)
                );
                setSellerRows([
                    mapApiFieldsToRow('sellers-total', 'Total', branch, true),
                    ...Object.entries(salespeople).map(([code, fields]) =>
                        mapApiFieldsToRow(code, fields.salespersonName, fields)
                    ),
                ]);
            } catch (err) {
                Snackbar.error('Erro ao buscar o relatório de vendas da loja.');
            } finally {
                setManagerLoading(false);
            }
        };

        fetchManagerReport();
    }, [
        checkIfCanMakeRequests,
        formattedBranchCode,
        initialDate,
        finalDate,
        isGerenteFoxton,
    ]);

    useEffect(() => {
        if (!isVendedorFoxton || !salesCode) return;
        if (!checkIfCanMakeRequests()) return;

        const fetchSellerReport = async () => {
            setSellerLoading(true);
            try {
                const response = await maisVendasApi.get(
                    'salesperson/sales-report',
                    {
                        params: {
                            dateInit: moment(initialDate).format('YYYY-MM-DD'),
                            dateEnd: moment(finalDate).format('YYYY-MM-DD'),
                            salesCode,
                        },
                    }
                );

                setPersonalRow(
                    mapApiFieldsToRow('personal', sellerName, response.data)
                );
            } catch (err) {
                Snackbar.error('Erro ao buscar o relatório de vendas.');
            } finally {
                setSellerLoading(false);
            }
        };

        fetchSellerReport();
    }, [
        checkIfCanMakeRequests,
        salesCode,
        initialDate,
        finalDate,
        isVendedorFoxton,
        sellerName,
    ]);

    if (!isGerenteFoxton && !isVendedorFoxton) {
        return <Redirect to={getDefaultRouteForUser(profile)} />;
    }

    return (
        <>
            <Header />

            <Style.Container container>
                <Grid item xs={12}>
                    <Style.Title>Relatório Real Time NOW</Style.Title>
                </Grid>
            </Style.Container>

            <DateRangePicker assessment excludePeriods={['month']} />

            <Style.Container container>
                <Grid item xs={12}>
                    <Style.Notice>
                        Atenção! Os nomes são exibidos apenas após a entrada de
                        movimentações.
                    </Style.Notice>
                </Grid>

                {isGerenteFoxton && (
                    <>
                        <Grid item xs={12}>
                            <ReportSectionCard title="Minha Loja">
                                {managerLoading || !storeRow ? (
                                    <Style.LoadingWrapper>
                                        <CircularProgress size={28} />
                                    </Style.LoadingWrapper>
                                ) : (
                                    <ReportTable
                                        rows={[storeRow]}
                                        columns={STORE_REPORT_COLUMNS}
                                        onSssgClick={row =>
                                            openSssgModal(row, 'store')
                                        }
                                    />
                                )}
                            </ReportSectionCard>
                        </Grid>

                        <Grid item xs={12}>
                            <ReportSectionCard title="Vendedores">
                                {managerLoading || !sellerRows ? (
                                    <Style.LoadingWrapper>
                                        <CircularProgress size={28} />
                                    </Style.LoadingWrapper>
                                ) : (
                                    <ReportTable
                                        rows={sellerRows}
                                        columns={VENDEDORES_REPORT_COLUMNS}
                                        onSssgClick={row =>
                                            openSssgModal(
                                                row,
                                                row.isTotal ? 'store' : 'seller'
                                            )
                                        }
                                    />
                                )}
                            </ReportSectionCard>
                        </Grid>
                    </>
                )}

                {isVendedorFoxton && (
                    <Grid item xs={12}>
                        <ReportSectionCard title="Minhas Vendas">
                            {sellerLoading || !personalRow ? (
                                <Style.LoadingWrapper>
                                    <CircularProgress size={28} />
                                </Style.LoadingWrapper>
                            ) : (
                                <ReportTable
                                    rows={[personalRow]}
                                    onSssgClick={row =>
                                        openSssgModal(row, 'seller')
                                    }
                                />
                            )}
                        </ReportSectionCard>
                    </Grid>
                )}
            </Style.Container>

            <SSSGDetailsModal
                open={sssgModal.open}
                onClose={closeSssgModal}
                variant={sssgModal.variant}
                sellerName={sssgModal.row?.nome}
                details={sssgModal.row?.sssgDetails}
            />
        </>
    );
}

export default RealTimeNow;
